import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Column } from "@ant-design/charts";
import { useTranslation } from "react-i18next";
import { DatePicker, Select, Table } from "antd";
import { ColumnsType, TablePaginationConfig } from "antd/lib/table";
import moment from "moment";
import { RangePickerProps } from "antd/lib/date-picker";
import { Link } from "react-router-dom";
import { Card } from "../../../components/ui";
import api from "../../../services/api";
import { useToast } from "../../../hooks/toast";

import "./styles.less";
import { IUser } from "../../Users";
import filterOptions from "../../../utils/filterOptions";

const { RangePicker } = DatePicker;

interface IStat {
  month: string;
  estimated: number;
  wasted: number;
}

interface IConsultant {
  id: string;
  short_description: string;
  identifier: string;
  contractor_name: string;
  estimated: number;
  wasted: number;
  project_short_description: string;
  project_identifier: string;
  activity: string;
  project_id: string;
}

interface IConsultantProductivityStats {
  stats: IStat[];
  consultants: IConsultant[];
}
const ConsultantsProductivityStats: React.FC = () => {
  const { t } = useTranslation(["activities", "dashboards"]);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IConsultantProductivityStats>();
  const [startMonth, setStartMonth] = useState(`01-${moment().format("YYYY")}`);
  const [endMonth, setEndMonth] = useState(
    `01-${moment().add(1, "year").format("YYYY")}`,
  );
  const [users, setUsers] = useState<IUser[]>([]);
  const [userFilter, setUserFilter] = useState(undefined);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const { addToast } = useToast();

  useEffect(() => {
    setLoadingUsers(true);
    api
      .get<IUser[]>("/users")
      .then(response => {
        setUsers(response.data.sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os consultores, tente novamente.",
        });
      })
      .finally(() => setLoadingUsers(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pagination: TablePaginationConfig = useMemo(
    () => ({
      defaultCurrent: 1,
      total: data?.consultants.length,
      defaultPageSize: 10,
    }),
    [data],
  );

  const usersOptions = useMemo(() => {
    if (!users) {
      return undefined;
    }

    return users.map(user => ({
      label: user.name,
      value: user.id,
    }));
  }, [users]);

  useEffect(() => {
    setLoading(true);
    api
      .get<IConsultantProductivityStats>(
        "/dashboards/consultants-productivity",
        {
          params: {
            start_month: startMonth,
            end_month: endMonth,
            consultant_id: userFilter,
          },
        },
      )
      .then(response => setData(response.data))
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os projetos, tente novamente.",
        });
      })
      .finally(() => setLoading(false));
  }, [addToast, endMonth, startMonth, userFilter]);

  const stats = useMemo(
    () =>
      data?.stats.flatMap(({ estimated, month, wasted }) => [
        {
          month: moment(month, "MM-YYYY").format("MMMM YYYY"),
          name: t("estimated"),
          value: estimated,
        },
        {
          month: moment(month, "MM-YYYY").format("MMMM YYYY"),
          name: t("wasted"),
          value: wasted,
        },
      ]) || [],
    [data?.stats, t],
  );

  const config = {
    data: stats,
    title: "Test",
    isGroup: true,
    xField: "month",
    yField: "value",
    seriesField: "name",
    height: 250,
    /** 设置颜色 */
    color: ["#3389eb", "#f39016ff"],

    /** 设置间距 */
    // marginRatio: 0.1,
    // label: {
    //   // 可手动配置 label 数据标签位置
    //   position: "middle",
    //   // 'top', 'middle', 'bottom'
    //   // 可配置附加的布局方法
    //   layout: [
    //     // 柱形图数据标签位置自动调整
    //     {
    //       type: "interval-adjust-position",
    //     }, // 数据标签防遮挡
    //     {
    //       type: "interval-hide-overlap",
    //     }, // 数据标签文颜色自动调整
    //     {
    //       type: "adjust-color",
    //     },
    //   ],
    // },
  };

  //   --dark-orange: #e95011ff;
  // --light-orange: #f39016ff;
  // --dark-grey: #242424ff;
  const consultantsColumns: ColumnsType<IConsultant> = [
    {
      title: t("name"),
      dataIndex: "name",
    },
    {
      title: t("estimated"),
      dataIndex: "estimated",
    },
    {
      title: t("wasted"),
      dataIndex: "wasted",
    },
    {
      title: t("completion_month"),
      dataIndex: "completion_month",
      render: (month: string) => moment(month, "MM-YYYY").format("MMMM YYYY"),
    },
    {
      title: t("activity"),
      dataIndex: "activity",
    },
    {
      title: t("project_identifier"),
      dataIndex: "project_identifier",
    },
    {
      title: t("project_short_description"),
      dataIndex: "project_short_description",
      render: (description: string, record) => {
        return (
          <Link to={`/app/projects/${record.project_id}`}>{description}</Link>
        );
      },
    },
  ];

  const onChangeDates: RangePickerProps["onChange"] = useCallback(
    (dates, _) => {
      if (dates) {
        setStartMonth(dates[0].format("MM-YYYY"));
        setEndMonth(dates[1].format("MM-YYYY"));
      }
    },
    [],
  );

  const disabledDate: RangePickerProps["disabledDate"] = current => {
    // Can not select days before today and today
    return current && current > moment().endOf("month");
  };

  const handleSelectUser = useCallback(user => setUserFilter(user), []);

  return (
    <Card
      loading={loading}
      extra={
        <>
          <RangePicker
            onChange={onChangeDates}
            format="MMMM YYYY"
            disabledDate={disabledDate}
            picker="month"
            allowClear={false}
            value={[moment(startMonth, "MM-YYYY"), moment(endMonth, "MM-YYYY")]}
          />
        </>
      }
      title={t("consultants-productivity", { ns: "dashboards" })}
    >
      {/* <Col lg={6} md={24} xl={6}> */}
      <Select
        allowClear
        showSearch
        filterOption={filterOptions}
        onChange={handleSelectUser}
        style={{ width: 250, marginBottom: 20 }}
        options={usersOptions}
        placeholder="Filtro por consultor"
        loading={loadingUsers}
        value={userFilter}
      />
      <Column {...config} />
      {/* </Col> */}
      {/* <Col lg={18} md={24} xl={18}> */}
      <Table
        className="table-striped"
        style={{ marginTop: 20 }}
        scroll={{
          x: "max-content",
          // y: maxHeight,
        }}
        dataSource={data?.consultants || []}
        rowKey={record => record.id}
        columns={consultantsColumns}
        pagination={pagination}
        size="small"
      />
      {/* </Col> */}
    </Card>
  );
};

export default ConsultantsProductivityStats;
